import { ReactNode } from "react"
import RichTooltip from "~/ui-rtk/components/ui/common/RichTooltip"
import { InfoSvg } from "~/ui-rtk/components/ui/svg/essentials/InfoSvg"
import {
  CATEGORY_COLORS,
  CATEGORY_BG_COLORS,
  ChannelType,
  MetricMetadata,
} from "~/ui-rtk/constants/metric-dictionary"
import { cn } from "~/ui-rtk/utils/tailwind-utils"

export type MetricTooltipData = Pick<
  MetricMetadata,
  | "key"
  | "title"
  | "description"
  | "category"
  | "platform"
  | "channelType"
  | "format"
>

type MetricTooltipProps = {
  /** Metric metadata to display */
  metric: MetricTooltipData
  /** Content to wrap with the tooltip */
  children?: ReactNode
  /** Whether to show the info icon */
  showIcon?: boolean
  /** Size of the info icon in pixels */
  iconSize?: number
  /** Tooltip placement */
  place?: "top" | "right" | "bottom" | "left"
  /** Additional class names for the wrapper */
  className?: string
}

/**
 * MetricTooltip - Displays metric information in a tooltip
 *
 * Wraps content with a tooltip showing metric description,
 * categories, platform, and other metadata.
 */
export default function MetricTooltip({
  metric,
  children,
  showIcon = true,
  iconSize = 14,
  place = "top",
  className,
}: MetricTooltipProps) {
  const tooltipContent = (
    <div className="max-w-xs">
      {/* Title */}
      <div className="font-semibold text-white mb-1">{metric.title}</div>

      {/* Description */}
      {metric.description && (
        <p className="text-sm text-gray-300 mb-2">{metric.description}</p>
      )}

      {/* Badges */}
      <div className="flex flex-wrap gap-1 mt-2">
        {/* Category badges */}
        {metric.category?.map(cat => (
          <span
            key={cat}
            className="px-2 py-0.5 rounded text-xs font-medium"
            style={{
              backgroundColor: CATEGORY_BG_COLORS[cat],
              color: CATEGORY_COLORS[cat],
            }}
          >
            {cat}
          </span>
        ))}

        {/* Platform badge */}
        {metric.platform && (
          <span className="px-2 py-0.5 rounded text-xs bg-system-blue-700 text-white">
            {metric.platform}
          </span>
        )}

        {/* Channel type badge */}
        {metric.channelType && metric.channelType !== ChannelType.NA && (
          <span className="px-2 py-0.5 rounded text-xs bg-system-blue-600 text-gray-200">
            {metric.channelType}
          </span>
        )}

        {/* Format badge */}
        {metric.format && (
          <span className="px-2 py-0.5 rounded text-xs bg-gray-700 text-gray-300">
            {metric.format}
          </span>
        )}
      </div>
    </div>
  )

  return (
    <RichTooltip content={tooltipContent} place={place}>
      <span
        className={cn("inline-flex items-center gap-1 cursor-help", className)}
      >
        {children}
        {showIcon && (
          <InfoSvg
            size={iconSize}
            fill="gray"
            className="hover:opacity-75 transition-opacity flex-shrink-0"
          />
        )}
      </span>
    </RichTooltip>
  )
}

/**
 * MetricInfoIcon - Standalone info icon with metric tooltip
 * Use when you just want an info icon that shows metric details
 */
export function MetricInfoIcon({
  metric,
  size = 14,
  place = "top",
  className,
}: {
  metric: MetricTooltipData
  size?: number
  place?: "top" | "right" | "bottom" | "left"
  className?: string
}) {
  return (
    <MetricTooltip metric={metric} showIcon={false} place={place}>
      <InfoSvg
        size={size}
        fill="gray"
        className={cn(
          "hover:opacity-75 transition-opacity cursor-help",
          className,
        )}
      />
    </MetricTooltip>
  )
}
